import React from 'react';
import Loadable from 'react-loadable';
import { useCookieStore } from '@uncinc/uncinc-react-kitchen-sink';
import {
  COOKIE_CONSENT_CATEGORIES,
  COOKIE_CONSENT_STATE,
} from '@uncinc/uncinc-react-kitchen-sink/dist/components/GoogleConsentCookieBanner';
import { LocalConsentStatus } from '@uncinc/uncinc-react-kitchen-sink/dist/components/GoogleConsentCookieBanner/types';

const CookieLazy = Loadable({
  loader: () => import('./lazy'),
  loading: () => null,
});

/**
 * TODO: This wrapper component is a temporary workaround for a flash issue in GoogleConsentCookieBanner.
 *
 * ISSUE: GoogleConsentCookieBanner from @uncinc/uncinc-react-kitchen-sink renders first, then checks
 * consent state, causing a visible flash of the banner on page refresh even when consent is already given.
 *
 * WORKAROUND: This wrapper checks consent cookies BEFORE rendering the banner component, preventing the flash.
 *
 * FIX IN KITCHEN-SINK: The GoogleConsentCookieBanner component itself should check consent state before
 * rendering and return null if consent is already given (and not editing). This would eliminate the need
 * for wrapper components in every project.
 *
 * WHEN FIXED: Remove this wrapper logic and use GoogleConsentCookieBanner directly in lazy.tsx
 *
 * See: https://github.com/uncinc/uncinc-react-kitchen-sink (create issue/PR)
 */
const Cookie = () => {
  const [consentCategories] = useCookieStore(COOKIE_CONSENT_CATEGORIES);
  const [consentState] = useCookieStore(COOKIE_CONSENT_STATE);

  // Check if user is currently editing their consent
  let isEditing = false;
  if (consentState) {
    try {
      const stateData = JSON.parse(consentState);
      isEditing = stateData.status === LocalConsentStatus.STATUS_EDITING;
    } catch (e) {
      // If parsing fails, assume not editing
      isEditing = false;
    }
  }

  // Check if user has already made consent choices
  let hasConsentDecision = false;
  if (consentCategories) {
    try {
      const categoriesData = JSON.parse(consentCategories);
      // If categories array exists and has content, user has made a choice
      hasConsentDecision = categoriesData.categories && categoriesData.categories.length > 0;
    } catch (e) {
      // If parsing fails, show banner to be safe
      hasConsentDecision = false;
    }
  }

  // Show banner if:
  // - User hasn't made a consent decision yet, OR
  // - User is currently editing their consent
  const shouldShowBanner = !hasConsentDecision || isEditing;

  if (!shouldShowBanner) {
    return null;
  }

  return <CookieLazy />;
};

export default Cookie;
